/**
 * @page /portal/abwesenheiten
 * @description Unified Abwesenheiten — alle 17 Abwesenheitsarten in einer Seite.
 *   Ersetzt PortalUrlaub + PortalKrankmeldung (v2.244.0).
 * @features
 *   - KPI-Cards: Urlaubsanspruch, Verbraucht, Saldo, Offene Anträge
 *   - Tab 1: Meine Abwesenheiten (Tabelle aller Typen + Jahresübersicht)
 *   - Tab 2: Neuer Antrag (Typ-Auswahl Grid + Formular)
 *   - Tab 3: Team-Kalender (Monatsansicht)
 * @api GET /api/portal/urlaub/kontingent, GET /api/portal/meine-abwesenheiten
 * @api GET /api/portal/abwesenheitsarten, POST /api/portal/urlaub/antraege
 * @api POST /api/portal/abwesenheit, PUT /api/portal/abwesenheit/:id/storno
 * @api PUT /api/portal/urlaub/antraege/:id/storno
 * @api GET /api/portal/urlaub/kalender, GET /api/portal/urlaub/feiertage
 * @api PUT /api/portal/krankmeldung/:id/dokument
 * @roles mitarbeiter, admin, manager
 * @i18n portal.absences
 * @since v2.244.0
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Clock, CheckCircle, XCircle, Ban, Umbrella, Calendar, ChevronLeft, ChevronRight,
  Users, Plus, Upload, FileText, AlertCircle, X,
  Palmtree, Thermometer, School, HeartHandshake, Shield, Baby, Stethoscope,
  BookOpen, Building2, UserCheck, Star, ShieldAlert, CalendarOff, Heart, GraduationCap
} from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import useTabParam from '../hooks/useTabParam';

const LUCIDE_ICONS = {
  Palmtree, Thermometer, School, HeartHandshake, Clock, Shield, Baby, Stethoscope,
  BookOpen, Building2, UserCheck, Star, ShieldAlert, Calendar, FileText, Umbrella,
  CalendarOff, Heart, GraduationCap,
};

function AbsenceIcon({ name, color, size = 24 }) {
  const Comp = LUCIDE_ICONS[name] || Calendar;
  return <Comp size={size} style={{ color }} />;
}
import { useLanguage } from '../context/LanguageContext';
import ConfirmDialog from '../components/ConfirmDialog';
import { berechnePlanbarAnteile, naechsterUjStart } from '../utils/urlaubPlanbar';

const API = import.meta.env.VITE_API_URL || '';

function fmtDate(d) {
  if (!d) return '–';
  const dt = typeof d === 'string' ? d.slice(0, 10) : d;
  const [y, m, day] = dt.split('-');
  return `${day}.${m}.${y}`;
}

// #1771: Jahresanspruch ohne unnötige ",0" (25 statt 25,0), Halbtage bleiben sichtbar.
function fmtTage(n) {
  return Number(n).toLocaleString('de-AT', { maximumFractionDigits: 1 });
}

const STATUS_STYLES = {
  beantragt:  { color: 'bg-amber-100 text-amber-700', Icon: Clock },
  genehmigt:  { color: 'bg-green-100 text-green-700', Icon: CheckCircle },
  aktiv:      { color: 'bg-green-100 text-green-700', Icon: CheckCircle },
  abgelehnt:  { color: 'bg-red-100 text-red-700', Icon: XCircle },
  storniert:  { color: 'bg-gray-100 text-[var(--text-tertiary)]', Icon: Ban },
  abgeschlossen: { color: 'bg-blue-100 text-blue-700', Icon: CheckCircle },
};

export default function PortalAbwesenheiten() {
  const { t, language } = useLanguage();
  const [tab, setTab] = useTabParam('liste');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Data
  const [kontingent, setKontingent] = useState(null);
  const [monatsSaldi, setMonatsSaldi] = useState([]);
  const [abwesenheiten, setAbwesenheiten] = useState([]);
  const [abwesenheitsarten, setAbwesenheitsarten] = useState([]);

  // New request state
  const [selectedArt, setSelectedArt] = useState(null);
  const [form, setForm] = useState({ von: '', bis: '', halber_tag: false, notiz: '', typ: 'urlaub' });
  const [berechneTage, setBerechneTage] = useState(null);
  const [projectedSaldo, setProjectedSaldo] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [uploadFile, setUploadFile] = useState(null);
  // #927 — stundenweise (Arztbesuch etc.)
  const [editingId, setEditingId] = useState(null);
  const [ganztaegig, setGanztaegig] = useState(true);
  const [vonZeit, setVonZeit] = useState('');
  const [bisZeit, setBisZeit] = useState('');

  // Team calendar
  const today = new Date();
  const [kalenderMonat, setKalenderMonat] = useState(`${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`);
  const [kalenderData, setKalenderData] = useState(null);

  // Filter
  const [filterStatus, setFilterStatus] = useState('');
  const [confirmDialog, setConfirmDialog] = useState(null);

  // #1784 — Deep-Link aus der Entscheidungs-Benachrichtigung (?antrag=<id>&src=<quelle>).
  // src ist Pflicht, weil urlaub_antraege und abwesenheiten numerisch kollidieren (#1499).
  const [searchParams, setSearchParams] = useSearchParams();
  const [highlight, setHighlight] = useState(null);

  const jahr = new Date().getFullYear();

  // ── Load all data ──
  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [kRes, aRes, artRes, mRes] = await Promise.all([
        fetch(`${API}/api/portal/urlaub/kontingent`, { credentials: 'include' }).then(r => r.json()).catch(() => null),
        fetch(`${API}/api/portal/meine-abwesenheiten`, { credentials: 'include' }).then(r => r.json()),
        fetch(`${API}/api/portal/abwesenheitsarten`, { credentials: 'include' }).then(r => r.json()),
        fetch(`${API}/api/portal/urlaub/saldo-monate?jahr=${jahr}`, { credentials: 'include' }).then(r => r.json()).catch(() => null),
      ]);
      if (kRes?.error === 'no_profile' || aRes?.error === 'no_profile') {
        setError('no_profile');
        setLoading(false);
        return;
      }
      setKontingent(kRes);
      setAbwesenheiten(aRes?.data || (Array.isArray(aRes) ? aRes : []));
      setAbwesenheitsarten(artRes?.data || []);
      setMonatsSaldi(mRes?.data || []);
    } catch (err) {
      console.error('PortalAbwesenheiten load error:', err);
    } finally {
      setLoading(false);
    }
  }, [jahr]);

  useEffect(() => { load(); }, [load]);

  const SRC_TO_QUELLE = { urlaub_antrag: 'urlaub_antraege', abwesenheit: 'abwesenheiten' };

  useEffect(() => {
    const id = searchParams.get('antrag');
    if (!id || !abwesenheiten.length) return;
    const quelle = SRC_TO_QUELLE[searchParams.get('src')];
    const treffer = abwesenheiten.find(a => String(a.id) === id && (!quelle || a.quelle === quelle));
    if (treffer) {
      setFilterStatus('');
      setHighlight(`${treffer.quelle}-${treffer.id}`);
    }
    setSearchParams(prev => {
      const p = new URLSearchParams(prev);
      p.delete('antrag');
      p.delete('src');
      return p;
    }, { replace: true });
  }, [abwesenheiten, searchParams, setSearchParams]);

  useEffect(() => {
    if (!highlight) return;
    document.querySelector(`[data-antrag="${highlight}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [highlight]);

  // #927 — reset stundenweise wenn Art keine Zeit-Erfassung erlaubt
  useEffect(() => {
    if (!selectedArt?.erlaubt_stundenweise) {
      setGanztaegig(true);
      setVonZeit('');
      setBisZeit('');
    }
  }, [selectedArt]);

  // #927 — Dauer aus Zeit-Differenz (in Stunden)
  const dauerH = useMemo(() => {
    if (ganztaegig || !vonZeit || !bisZeit) return null;
    const [vh, vm] = vonZeit.split(':').map(Number);
    const [bh, bm] = bisZeit.split(':').map(Number);
    if ([vh, vm, bh, bm].some(n => Number.isNaN(n))) return null;
    const min = (bh * 60 + bm) - (vh * 60 + vm);
    return min > 0 ? min / 60 : null;
  }, [ganztaegig, vonZeit, bisZeit]);

  // ── Auto-calculate days via server API (holidays + MA schedule aware) ──
  useEffect(() => {
    if (form.halber_tag) {
      setBerechneTage(0.5);
      return;
    }
    if (!form.von || !form.bis || form.bis < form.von) {
      setBerechneTage(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API}/api/urlaub/berechne-arbeitstage?von=${form.von}&bis=${form.bis}`, { credentials: 'include' });
        if (!cancelled && res.ok) {
          const json = await res.json();
          setBerechneTage(json.tage ?? 0);
        }
      } catch { if (!cancelled) setBerechneTage(0); }
    })();
    return () => { cancelled = true; };
  }, [form.von, form.bis, form.halber_tag]);

  // ── Projected saldo for urlaub/ZA requests (v2.289.0) ──
  useEffect(() => {
    const isUrlaub = !selectedArt || selectedArt.key === 'urlaub' || selectedArt.key === 'zeitausgleich';
    if (!form.von || !isUrlaub) { setProjectedSaldo(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API}/api/portal/urlaub/kontingent?stichtag=${form.von}`, { credentials: 'include' });
        if (!cancelled && res.ok) setProjectedSaldo(await res.json());
      } catch { if (!cancelled) setProjectedSaldo(null); }
    })();
    return () => { cancelled = true; };
  }, [form.von, selectedArt]);

  // ── Filtered list ──
  const filteredAbwesenheiten = useMemo(() => {
    if (!filterStatus) return abwesenheiten;
    return abwesenheiten.filter(a => a.status === filterStatus);
  }, [abwesenheiten, filterStatus]);

  // ── Submit handler ──
  async function handleSubmit() {
    setSubmitting(true);
    setSubmitError('');
    try {
      const art = selectedArt;
      const stundenweiseAktiv = !!(art?.erlaubt_stundenweise && !ganztaegig);
      if (stundenweiseAktiv) {
        if (!vonZeit || !bisZeit) throw new Error(t('absence.time_required', 'Von und Bis Uhrzeit erforderlich'));
        if (dauerH == null || dauerH <= 0) throw new Error(t('absence.time_invalid_range', 'Bis-Zeit muss nach Von-Zeit liegen'));
      }

      // #927 — Edit-Modus: PUT statt POST (nur stundenweise via Self-Service)
      if (editingId) {
        const body = {
          von_datum: form.von,
          bis_datum: form.bis || form.von,
          grund: form.notiz,
          von_zeit: stundenweiseAktiv ? vonZeit : null,
          bis_zeit: stundenweiseAktiv ? bisZeit : null,
        };
        const res = await fetch(`${API}/api/portal/abwesenheit/${editingId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(body),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.error || t('portal.absences.submitError'));
      } else if (art?.key === 'krankenstand') {
        // #1785: Krankenstand → /api/portal/krankmeldung, identisch zum Mobile-Portal.
        //   Vorher lief der Desktop in den generischen Abwesenheits-Endpoint und
        //   erzeugte dort eine Zeile im Sackgassen-Status, die keine Zeitkonto-
        //   Berechnung liest. Nebeneffekt: :id ist jetzt garantiert eine
        //   urlaub_antraege-ID, der Arztnachweis kann nicht mehr fehlzugeordnet werden.
        const res = await fetch(`${API}/api/portal/krankmeldung`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            notiz: form.notiz || undefined,
            bis_voraussichtlich: form.bis || undefined,
          }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.error || t('portal.absences.submitError'));

        if (uploadFile && data?.data?.id) {
          const fd = new FormData();
          fd.append('file', uploadFile);
          const up = await fetch(`${API}/api/portal/krankmeldung/${data.data.id}/dokument?src=urlaub_antraege`, {
            method: 'PUT',
            credentials: 'include',
            body: fd,
          });
          if (!up.ok) throw new Error(t('portal.absences.uploadError', 'Krankmeldung gespeichert, aber der Nachweis konnte nicht hochgeladen werden. Bitte erneut hochladen.'));
        }
      } else if ((art?.key === 'urlaub' || art?.key === 'zeitausgleich' || !art) && !(art?.key === 'zeitausgleich' && stundenweiseAktiv)) {
        // Urlaub/ZA ganztägig → use urlaub/antraege endpoint
        // #1864 Folgeseite 3: STUNDENWEISER Zeitausgleich geht NICHT hier entlang —
        //   urlaub_antraege kennt keine Uhrzeit, die Zeiten gingen still verloren.
        //   Er fällt in den Zweig darunter (/api/portal/abwesenheit, genehmigungspflichtig).
        const res = await fetch(`${API}/api/portal/urlaub/antraege`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ ...form, typ: art?.key || 'urlaub' }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.error || t('portal.absences.submitError'));
      } else {
        // All other types → use abwesenheit endpoint
        const body = {
          art_key: art.key,
          von_datum: form.von,
          bis_datum: form.bis || form.von,
          grund: form.notiz,
        };
        if (stundenweiseAktiv) {
          body.von_zeit = vonZeit;
          body.bis_zeit = bisZeit;
        }
        const res = await fetch(`${API}/api/portal/abwesenheit`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(body),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.error || t('portal.absences.submitError'));
      }

      // Reset and reload
      setSelectedArt(null);
      setForm({ von: '', bis: '', halber_tag: false, notiz: '', typ: 'urlaub' });
      setUploadFile(null);
      setEditingId(null);
      setGanztaegig(true);
      setVonZeit('');
      setBisZeit('');
      setTab('liste');
      load();
    } catch (err) {
      setSubmitError(err.message);
    } finally {
      setSubmitting(false);
    }
  }

  // #927 — Edit-Handler für stundenweise Abwesenheiten
  function handleEdit(item) {
    const art = abwesenheitsarten.find(a => a.key === item.typ);
    if (!art) return;
    setSelectedArt(art);
    setForm({
      von: String(item.von_datum).slice(0, 10),
      bis: String(item.bis_datum || item.von_datum).slice(0, 10),
      halber_tag: false,
      notiz: item.notiz || '',
      typ: item.typ,
    });
    if (item.von_zeit && item.bis_zeit) {
      setGanztaegig(false);
      setVonZeit(String(item.von_zeit).slice(0, 5));
      setBisZeit(String(item.bis_zeit).slice(0, 5));
    } else {
      setGanztaegig(true);
      setVonZeit('');
      setBisZeit('');
    }
    setEditingId(item.id);
    setSubmitError('');
    setTab('antrag');
  }

  // ── Storno ──
  function handleStorno(item) {
    setConfirmDialog({
      title: t('portal.absences.confirmCancelTitle'),
      message: t('portal.absences.confirmCancelText'),
      variant: 'danger',
      confirmLabel: t('portal.absences.cancel'),
      cancelLabel: t('common.cancel'),
      onConfirm: async () => {
        setConfirmDialog(null);
        try {
          // Use correct endpoint based on source
          if (item.quelle === 'urlaub_antraege') {
            await fetch(`${API}/api/portal/urlaub/antraege/${item.id}/storno`, { method: 'PUT', credentials: 'include' });
          } else {
            await fetch(`${API}/api/portal/abwesenheit/${item.id}/storno`, { method: 'PUT', credentials: 'include' });
          }
          load();
        } catch { /* ignore */ }
      },
    });
  }

  // ── Team calendar ──
  const loadKalender = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/portal/urlaub/kalender?monat=${kalenderMonat}`, { credentials: 'include' });
      setKalenderData(await res.json());
    } catch { /* ignore */ }
  }, [kalenderMonat]);

  useEffect(() => { if (tab === 'kalender') loadKalender(); }, [tab, loadKalender]);

  // ── Year overview bars ──
  const yearBars = useMemo(() => {
    const bars = [];
    for (let m = 0; m < 12; m++) {
      let genehmigt = 0, offen = 0;
      for (const a of abwesenheiten) {
        const vonM = parseInt((a.von_datum || '').slice(5, 7)) - 1;
        const bisM = parseInt((a.bis_datum || a.von_datum || '').slice(5, 7)) - 1;
        if (vonM <= m && bisM >= m) {
          const tage = parseFloat(a.tage) || 0;
          if (['genehmigt', 'aktiv', 'abgeschlossen'].includes(a.status)) genehmigt += tage;
          else if (a.status === 'beantragt') offen += tage;
        }
      }
      bars.push({ monat: t(`common.months_short.${m}`), genehmigt, offen });
    }
    return bars;
  }, [abwesenheiten, t]);

  // ── No-profile state ──
  if (error === 'no_profile') {
    return (
      <div className="p-8">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-xl bg-[#338edc]/10 flex items-center justify-center">
            <Calendar size={20} className="text-[#338edc]" />
          </div>
          <h1 className="text-2xl font-bold text-[var(--text-primary)]">{t('portal.absences.title')}</h1>
        </div>
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <div className="w-16 h-16 rounded-2xl bg-[var(--bg-inset,#F8F9FC)] flex items-center justify-center mb-4">
            <Calendar size={28} className="text-[var(--text-muted)]" />
          </div>
          <h3 className="text-base font-semibold text-[var(--text-primary)] mb-1">{t('portal.noProfile')}</h3>
          <p className="text-sm text-[var(--text-tertiary)] max-w-sm">{t('portal.noProfileDesc')}</p>
        </div>
      </div>
    );
  }

  // ── Loading ──
  if (loading) {
    return (
      <div className="p-8">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-[var(--bg-inset,#F8F9FC)] rounded w-1/3" />
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map(i => <div key={i} className="h-24 bg-[var(--bg-inset,#F8F9FC)] rounded-xl" />)}
          </div>
          <div className="h-64 bg-[var(--bg-inset,#F8F9FC)] rounded-xl" />
        </div>
      </div>
    );
  }

  const tabs = [
    { key: 'liste', label: t('portal.absences.tabList') },
    { key: 'antrag', label: t('portal.absences.tabNew') },
    { key: 'kalender', label: t('portal.absences.tabCalendar') },
  ];

  return (
    <div className="p-8">
      {/* Page Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#338edc]/10 flex items-center justify-center">
            <Calendar size={20} className="text-[#338edc]" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-[var(--text-primary)]">{t('portal.absences.title')}</h1>
            <p className="text-sm text-[var(--text-secondary)] mt-1">{t('portal.absences.subtitle')}</p>
          </div>
        </div>
        <button
          onClick={() => { setTab('antrag'); setSelectedArt(null); }}
          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#338edc] hover:bg-[#2b7bc4] text-white text-sm font-semibold transition-colors"
        >
          <Plus size={16} /> {t('portal.absences.newRequest')}
        </button>
      </div>

      {/* #1769: vereinfachte Ansicht — Hero (planbar bis UJ-Ende) + 2 Kontext-Cards.
          Reine Anzeige aus dem Kontingent-Response, keine neue Berechnung. */}
      {kontingent && (() => {
        const fmt1 = n => Number(n).toLocaleString('de-AT', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
        // planbar = bestehende verfuegbar_planbar-Zahl — NICHT neu gerechnet.
        const { planbar, realVerfuegbar, vorgriffAnteil } = berechnePlanbarAnteile(kontingent);
        const standHeute = Number(kontingent.aktuell_verfuegbar ?? kontingent.saldo ?? 0);
        // #1769: verplant zeigt NUR das laufende Urlaubsjahr; der Rest wird als
        // Hinweis ausgewiesen statt den Saldo des laufenden UJ zu belasten.
        const geplantGesamt = Number(kontingent.geplant ?? 0);
        const verplantUj = Number(kontingent.geplant_uj ?? kontingent.geplant ?? 0);
        const naechstesUj = Math.round(Math.max(0, geplantGesamt - verplantUj) * 100) / 100;
        // #1748: Überplanung kommt aus dem SSoT-Payload; Fallback rechnet sie aus
        // den Anzeige-Feldern nach, damit die Warnung nie stillschweigend entfällt.
        const up = kontingent.ueberplanung || null;
        const freiFallback = kontingent.verfuegbar_nach_geplant != null
          ? Number(kontingent.verfuegbar_nach_geplant)
          : Math.round((standHeute - verplantUj) * 100) / 100;
        const rawPlanbar = up
          ? Number(up.raw_planbar)
          : Math.round((freiFallback + Number(kontingent.vorgriff_effektiv ?? 0)) * 100) / 100;
        const ueberzogenUm = up ? Number(up.ueberzogen_um) : Math.max(0, Math.round(-rawPlanbar * 100) / 100);
        const istUeberplant = ueberzogenUm > 0;
        const skala = Math.max(planbar, 0.01);
        const pctReal = Math.min(100, (Math.max(0, realVerfuegbar) / skala) * 100);
        const pctVorgriff = Math.min(100 - pctReal, (vorgriffAnteil / skala) * 100);
        const vorgriffPattern = 'repeating-linear-gradient(45deg, #8fc3ec, #8fc3ec 4px, rgba(143,195,236,0.35) 4px, rgba(143,195,236,0.35) 8px)';
        // #1772: HR kann jede Card pro MA ausblenden (Default AN). Reine Sicht-
        // barkeit — Werte und die harte Antrags-Validierung bleiben unberührt.
        const zeigePlanbar = kontingent.portal_zeige_planbar !== false;
        const zeigeSaldo = kontingent.portal_zeige_saldo !== false;
        const zeigeVerplant = kontingent.portal_zeige_verplant !== false;
        // #1808: Desktop-Paritaet zu MobileAbwesend — Light-Modus blendet nur die
        // grafische Aufteilung aus, die Gesamtzahl bleibt sichtbar.
        const lightModus = kontingent.app_light_modus === true;
        if (!zeigePlanbar && !zeigeSaldo && !zeigeVerplant) return null;
        return (
          <div className="mb-6">
            {/* #1748: Überplanungs-Banner — geplant übersteigt Saldo + zulässigen Vorgriff */}
            {zeigePlanbar && istUeberplant && (
              <div className="rounded-xl border px-4 py-3 mb-4"
                style={up && up.severity === 'fehler'
                  ? { background: '#FEE2E2', borderColor: '#FECACA' }
                  : { background: '#FEF3C7', borderColor: '#FDE68A' }}>
                <p className="text-sm leading-snug" style={{ color: up && up.severity === 'fehler' ? '#DC2626' : '#92400E' }}>
                  {t('portal.absences.ueberplantBanner', {
                    tage: fmt1(ueberzogenUm),
                    defaultValue: 'Achtung: Du hast {{tage}} Tage über deinen verfügbaren Urlaub (inkl. Vorgriff) hinaus verplant. Diese Tage sind noch nicht gedeckt.',
                  })}
                </p>
              </div>
            )}
            {/* Hero: Zum Planen verfügbar */}
            {zeigePlanbar && (
            <div className="border-2 border-[var(--border-default,#E5E7EB)] rounded-xl p-5 mb-4">
              <div className="flex items-center gap-2 mb-1">
                <Palmtree size={16} className="text-[#338edc]" />
                <p className="text-xs uppercase tracking-wider font-medium text-[var(--text-secondary)]">{t('portal.absences.planHero.title', 'Zum Planen verfügbar')}</p>
              </div>
              <p className="text-3xl font-bold text-[var(--text-primary)] mb-2">
                {fmt1(planbar)}{' '}
                <span className="text-sm font-normal text-[var(--text-tertiary)]">{t('portal.absences.tage', 'Tage')}</span>
              </p>
              {!lightModus && (<>
              <div className="h-2.5 rounded-full overflow-hidden flex max-w-xl" style={{ background: 'var(--bg-inset, #F8F9FC)', border: '1px solid var(--border-default, #E5E7EB)' }}>
                <div className="h-full" style={{ width: `${pctReal}%`, background: '#338edc' }} />
                {pctVorgriff > 0 && (
                  <div className="h-full" style={{ width: `${pctVorgriff}%`, background: vorgriffPattern }} />
                )}
              </div>
              <div className="flex items-center gap-4 mt-2">
                <span className="flex items-center gap-1.5 text-xs text-[var(--text-secondary)]">
                  <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ background: '#338edc' }} />
                  {t('portal.absences.planHero.legendeAufgebaut', { tage: fmt1(realVerfuegbar), defaultValue: '{{tage}} aus Guthaben' })}
                </span>
                <span className="flex items-center gap-1.5 text-xs text-[var(--text-secondary)]">
                  <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ background: vorgriffPattern }} />
                  {t('portal.absences.planHero.legendeVorgriff', { tage: fmt1(vorgriffAnteil), defaultValue: '{{tage}} im Vorgriff' })}
                </span>
              </div>
              </>)}
              <p className="text-xs text-[var(--text-muted)] mt-2">
                {t('portal.absences.planHero.hintBis', { datum: fmtDate(kontingent.urlaubsjahr_bis), defaultValue: 'bis {{datum}} zum Planen verfügbar' })}
              </p>
              {/* #1771: positiver Ausblick statt Austritts-Hinweis — beide Werte kommen aus dem Payload */}
              {naechsterUjStart(kontingent.urlaubsjahr_bis) && kontingent.anspruch != null && (
                <p className="text-xs text-[var(--text-muted)] mt-1">
                  {t('portal.absences.planHero.hintNeuesUj', {
                    datum: fmtDate(naechsterUjStart(kontingent.urlaubsjahr_bis)),
                    tage: fmtTage(kontingent.anspruch),
                    defaultValue: 'Ab {{datum}} kommen {{tage}} Tage zum Planen dazu',
                  })}
                </p>
              )}
            </div>
            )}
            {/* #1769: 2 Kontext-Cards — Saldo jetzt / Bereits verplant bis Ende UJ */}
            {(zeigeSaldo || zeigeVerplant) && (
            <div className={`grid ${zeigeSaldo && zeigeVerplant ? 'grid-cols-2' : 'grid-cols-1 max-w-md'} gap-4 mb-2`}>
              {zeigeSaldo && (
              <div className="border border-[var(--border-default,#E5E7EB)] rounded-xl p-5">
                <p className="text-xs uppercase tracking-wider font-medium text-[var(--text-secondary)] mb-1">{t('portal.absences.karteSaldoJetzt', 'Saldo jetzt')}</p>
                <p className="text-2xl font-bold text-[var(--text-primary)]">{fmt1(standHeute)}</p>
                <p className="text-sm text-[var(--text-muted)] mt-1">{t('portal.absences.standHeute')}</p>
              </div>
              )}
              {zeigeVerplant && (
              <div className="border border-[var(--border-default,#E5E7EB)] rounded-xl p-5">
                <p className="text-xs uppercase tracking-wider font-medium text-[var(--text-secondary)] mb-1">{t('portal.absences.karteVerplantUj', 'Bereits verplant')}</p>
                <p className="text-2xl font-bold text-[var(--text-primary)]">{fmt1(verplantUj)}</p>
                <p className="text-sm text-[var(--text-muted)] mt-1">{t('portal.absences.bisDatum', { datum: fmtDate(kontingent.urlaubsjahr_bis) })}</p>
              </div>
              )}
            </div>
            )}
            {zeigeVerplant && naechstesUj > 0 && (
              <p className="text-xs text-[var(--text-muted)]">
                {t('portal.absences.hinweisNaechstesUj', { tage: fmt1(naechstesUj), defaultValue: '{{tage}} Tage im nächsten Urlaubsjahr' })}
              </p>
            )}
          </div>
        );
      })()}

      {/* Monats-Timeline (#889) */}
      {monatsSaldi.length > 0 && (() => {
        const maxAbs = Math.max(1, ...monatsSaldi.map(r => Math.abs(Number(r.saldo_tage))));
        const monateFull = Array.from({ length: 12 }, (_, i) => {
          const found = monatsSaldi.find(r => Number(r.monat) === i + 1);
          return found || { monat: i + 1, jahr, aufgebaut_kumulativ: 0, genommen_kumulativ: 0, saldo_tage: 0, _empty: true };
        });
        return (
          <div className="border border-[var(--border-default,#E5E7EB)] rounded-xl p-5 mb-6">
            <div className="flex items-center justify-between mb-3">
              <div>
                <p className="text-xs uppercase tracking-wider font-medium text-[var(--text-secondary)]">{t('urlaub.monatsTimeline.title', 'Monats-Aufbau')}</p>
                <p className="text-sm text-[var(--text-muted)] mt-0.5">{t('urlaub.monatsTimeline.hint', 'Saldo je Monat (+ Aufbau, − Genommen, ± Rest-Vorjahr)')}</p>
              </div>
              <div className="text-xs text-[var(--text-tertiary)]">{jahr}</div>
            </div>
            <div className="flex items-end gap-2 h-24">
              {monateFull.map(row => {
                const saldo = Number(row.saldo_tage);
                const height = row._empty ? 2 : Math.max(3, (Math.abs(saldo) / maxAbs) * 80);
                const color = row._empty ? 'bg-[var(--border-default,#E5E7EB)]' : saldo < 0 ? 'bg-red-400' : saldo < 3 ? 'bg-amber-400' : 'bg-emerald-400';
                const label = ['Jan','Feb','Mär','Apr','Mai','Jun','Jul','Aug','Sep','Okt','Nov','Dez'][row.monat - 1];
                return (
                  <div key={row.monat} className="flex-1 flex flex-col items-center justify-end" title={row._empty ? '' : `${label}: Aufgebaut ${Math.round(Number(row.aufgebaut_kumulativ)*100)/100} / Genommen ${Math.round(Number(row.genommen_kumulativ)*100)/100} → Saldo ${Math.round(saldo*100)/100}`}>
                    <div className={`w-full rounded-t ${color}`} style={{ height: `${height}px` }} />
                    <div className="text-[10px] text-[var(--text-tertiary)] mt-1">{label}</div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}

      {/* Tab Bar */}
      <div className="bg-[var(--bg-inset,#F8F9FC)] rounded-[10px] p-[3px] border border-[var(--border-default,#E5E7EB)] inline-flex gap-1 mb-6">
        {tabs.map(t2 => (
          <button
            key={t2.key}
            className={`px-4 py-2 rounded-[8px] text-sm font-medium transition-all ${
              tab === t2.key
                ? 'bg-white shadow-sm text-[var(--text-primary)] font-semibold dark:bg-[var(--bg-card)]'
                : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)]'
            }`}
            onClick={() => setTab(t2.key)}
          >
            {t2.label}
          </button>
        ))}
      </div>

      {/* ── TAB 1: Meine Abwesenheiten ── */}
      {tab === 'liste' && (
        <>
          {/* Status Filter */}
          <div className="flex items-center gap-3 mb-4">
            <select
              value={filterStatus}
              onChange={e => setFilterStatus(e.target.value)}
              className="px-3 py-2 border border-[var(--border-default,#E5E7EB)] rounded-lg text-sm bg-[var(--bg-card,#fff)] text-[var(--text-primary)]"
            >
              <option value="">{t('portal.absences.allStatuses')}</option>
              <option value="beantragt">{t('portal.absences.statusPending')}</option>
              <option value="genehmigt">{t('portal.absences.statusApproved')}</option>
              <option value="aktiv">{t('portal.absences.statusActive')}</option>
              <option value="abgelehnt">{t('portal.absences.statusRejected')}</option>
              <option value="storniert">{t('portal.absences.statusCancelled')}</option>
            </select>
          </div>

          {/* Absences Table */}
          <div className="border border-[var(--border-default,#E5E7EB)] rounded-xl overflow-hidden mb-6">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-[var(--bg-inset,#F8F9FC)]">
                  <th className="px-4 py-3 text-left text-xs uppercase tracking-wider font-medium text-[var(--text-secondary)]">{t('portal.absences.type')}</th>
                  <th className="px-4 py-3 text-left text-xs uppercase tracking-wider font-medium text-[var(--text-secondary)]">{t('portal.absences.from')}</th>
                  <th className="px-4 py-3 text-left text-xs uppercase tracking-wider font-medium text-[var(--text-secondary)]">{t('portal.absences.to')}</th>
                  <th className="px-4 py-3 text-left text-xs uppercase tracking-wider font-medium text-[var(--text-secondary)]">{t('portal.absences.days')}</th>
                  <th className="px-4 py-3 text-left text-xs uppercase tracking-wider font-medium text-[var(--text-secondary)]">{t('portal.absences.status')}</th>
                  <th className="px-4 py-3 text-right text-xs uppercase tracking-wider font-medium text-[var(--text-secondary)]">{t('portal.absences.actions')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--border-default,#E5E7EB)]">
                {filteredAbwesenheiten.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-12 text-center text-sm text-[var(--text-tertiary)]">
                      {filterStatus ? t('portal.absences.noFiltered') : t('portal.absences.noEntries')}
                    </td>
                  </tr>
                ) : filteredAbwesenheiten.map((a, i) => {
                  const ss = STATUS_STYLES[a.status] || STATUS_STYLES.beantragt;
                  return (
                    <tr
                      key={`${a.quelle}-${a.id}-${i}`}
                      data-antrag={`${a.quelle}-${a.id}`}
                      className={`hover:bg-[var(--bg-inset,#F8F9FC)] transition-colors ${highlight === `${a.quelle}-${a.id}` ? 'bg-[#338edc]/10' : ''}`}
                    >
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full" style={{ backgroundColor: `${a.typ_farbe}20`, color: a.typ_farbe }}>
                          <AbsenceIcon name={a.typ_icon} color={a.typ_farbe} size={14} /> {a.typ_bezeichnung || a.typ}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-[var(--text-primary)]">
                        {fmtDate(a.von_datum)}
                        {a.von_zeit && a.bis_zeit && (
                          <span className="ml-1 text-xs text-[var(--text-tertiary)]">{String(a.von_zeit).slice(0, 5)}</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-[var(--text-primary)]">
                        {fmtDate(a.bis_datum)}
                        {a.von_zeit && a.bis_zeit && (
                          <span className="ml-1 text-xs text-[var(--text-tertiary)]">{String(a.bis_zeit).slice(0, 5)}</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-[var(--text-primary)] font-mono">
                        {a.von_zeit && a.bis_zeit
                          ? `${(parseFloat(a.stunden) || 0).toFixed(1).replace('.', ',')} h`
                          : (parseFloat(a.tage) === 0.5 ? '0,5' : parseFloat(a.tage || 0))}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${ss.color}`}>
                          {t(`portal.absences.status_${a.status}`) || a.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="inline-flex gap-1 items-center justify-end">
                          {a.status === 'beantragt' && a.erlaubt_stundenweise && a.quelle === 'abwesenheiten' && (
                            <button
                              onClick={() => handleEdit(a)}
                              className="text-xs text-[#338edc] hover:text-[#2b7bc4] font-medium px-2 py-1 rounded hover:bg-[#338edc]/10 transition-colors"
                            >
                              {t('absence.edit', 'Bearbeiten')}
                            </button>
                          )}
                          {a.status === 'beantragt' && (
                            <button
                              onClick={() => handleStorno(a)}
                              className="text-xs text-red-600 hover:text-red-700 font-medium px-2 py-1 rounded hover:bg-red-50 transition-colors"
                            >
                              {t('portal.absences.cancel')}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Year Overview */}
          <div className="border border-[var(--border-default,#E5E7EB)] rounded-xl p-5">
            <h3 className="text-sm font-semibold text-[var(--text-primary)] uppercase tracking-wider mb-4">{t('portal.absences.yearOverview', { year: jahr })}</h3>
            <div className="grid grid-cols-6 lg:grid-cols-12 gap-2">
              {yearBars.map((bar, i) => {
                const maxDays = 25;
                const gPct = Math.min(100, (bar.genehmigt / maxDays) * 100);
                const oPct = Math.min(100 - gPct, (bar.offen / maxDays) * 100);
                return (
                  <div key={i} className="text-center">
                    <div className="text-[9px] text-[var(--text-secondary)] mb-1">{bar.monat}</div>
                    <div className="h-16 bg-[var(--bg-inset,#F8F9FC)] rounded relative overflow-hidden flex flex-col-reverse">
                      {gPct > 0 && <div className="bg-emerald-500/50 rounded-b" style={{ height: `${gPct}%` }} />}
                      {oPct > 0 && <div className="bg-amber-500/50" style={{ height: `${oPct}%` }} />}
                    </div>
                    {(bar.genehmigt > 0 || bar.offen > 0) && (
                      <div className="text-[9px] text-[var(--text-tertiary)] mt-0.5 font-mono">
                        {(bar.genehmigt + bar.offen).toFixed(0)}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            <div className="flex items-center gap-4 mt-3 text-[10px] text-[var(--text-secondary)]">
              <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-emerald-500/50" /> {t('portal.absences.statusApproved')}</span>
              <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-amber-500/50" /> {t('portal.absences.statusPending')}</span>
            </div>
          </div>
        </>
      )}

      {/* ── TAB 2: Neuer Antrag ── */}
      {tab === 'antrag' && (
        <div>
          {!selectedArt ? (
            <>
              <h3 className="text-base font-semibold text-[var(--text-primary)] mb-4">{t('portal.absences.selectType')}</h3>
              <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {/* Urlaub + ZA are special — always shown */}
                <button
                  onClick={() => { setSelectedArt({ key: 'urlaub', label_de: 'Urlaub', label_en: 'Vacation', farbe: '#338edc', icon: 'Palmtree', braucht_genehmigung: true, kategorie: 'bezahlt' }); setForm(f => ({ ...f, typ: 'urlaub' })); }}
                  className="border border-[var(--border-default,#E5E7EB)] rounded-xl p-5 hover:border-[#338edc] cursor-pointer transition-all text-left group"
                >
                  <div className="mb-2"><AbsenceIcon name="Palmtree" color="#338edc" size={28} /></div>
                  <h4 className="font-semibold text-[var(--text-primary)]">{t('portal.absences.typeVacation')}</h4>
                  <p className="text-xs text-[var(--text-tertiary)] mt-1">{t('portal.absences.typeVacationDesc')}</p>
                  <span className="inline-block mt-2 px-2 py-0.5 text-[10px] font-medium rounded-full bg-green-100 text-green-700">{t('portal.absences.paid')}</span>
                </button>
                <button
                  onClick={() => { setSelectedArt({ key: 'zeitausgleich', label_de: 'Zeitausgleich', label_en: 'Time Off', farbe: '#338edc', icon: 'Clock', braucht_genehmigung: true, kategorie: 'bezahlt', /* #1864 F3: Stundenweise-Schalter haengt an der Stammdaten-Flagge, nie hart */ erlaubt_stundenweise: abwesenheitsarten.find(a => a.key === 'zeitausgleich')?.erlaubt_stundenweise === true }); setForm(f => ({ ...f, typ: 'zeitausgleich', halber_tag: false })); }}
                  className="border border-[var(--border-default,#E5E7EB)] rounded-xl p-5 hover:border-[#338edc] cursor-pointer transition-all text-left group"
                >
                  <div className="mb-2"><AbsenceIcon name="Clock" color="#338edc" size={28} /></div>
                  <h4 className="font-semibold text-[var(--text-primary)]">{t('portal.absences.typeTimeOff')}</h4>
                  <p className="text-xs text-[var(--text-tertiary)] mt-1">{t('portal.absences.typeTimeOffDesc')}</p>
                  <span className="inline-block mt-2 px-2 py-0.5 text-[10px] font-medium rounded-full bg-blue-100 text-blue-700">{t('portal.absences.paid')}</span>
                </button>
                {/* Dynamic types from abwesenheitsarten */}
                {abwesenheitsarten.filter(a => a.key !== 'urlaub' && a.key !== 'zeitausgleich').map(art => (
                  <button
                    key={art.key}
                    onClick={() => setSelectedArt(art)}
                    className="border border-[var(--border-default,#E5E7EB)] rounded-xl p-5 hover:border-[#338edc] cursor-pointer transition-all text-left group"
                  >
                    <div className="mb-2"><AbsenceIcon name={art.icon} color={art.farbe} size={28} /></div>
                    <h4 className="font-semibold text-[var(--text-primary)]">{language === 'de' ? art.label_de : art.label_en}</h4>
                    {art.max_tage_pro_jahr && (
                      <p className="text-xs text-[var(--text-tertiary)] mt-1">{t('portal.absences.maxDays', { n: art.max_tage_pro_jahr })}</p>
                    )}
                    <span className={`inline-block mt-2 px-2 py-0.5 text-[10px] font-medium rounded-full ${art.bezahlt ? 'bg-green-100 text-green-700' : 'bg-[var(--bg-inset,#F8F9FC)] text-[var(--text-tertiary,#6B7280)]'}`}>
                      {art.bezahlt ? t('portal.absences.paid') : t('portal.absences.unpaid')}
                    </span>
                  </button>
                ))}
              </div>
            </>
          ) : (
            /* ── Request Form ── */
            <div className="max-w-lg">
              <button
                onClick={() => { setSelectedArt(null); setEditingId(null); setSubmitError(''); }}
                className="flex items-center gap-1 text-sm text-[var(--text-secondary)] hover:text-[var(--text-primary)] mb-4"
              >
                <ChevronLeft size={16} /> {t('portal.absences.backToTypes')}
              </button>
              <div className="border border-[var(--border-default,#E5E7EB)] rounded-xl p-6">
                <div className="flex items-center gap-3 mb-6">
                  <AbsenceIcon name={selectedArt.icon} color={selectedArt.farbe} size={28} />
                  <div>
                    <h3 className="text-base font-semibold text-[var(--text-primary)]">
                      {editingId ? t('absence.edit_title', 'Bearbeiten') + ': ' : ''}
                      {language === 'de' ? selectedArt.label_de : (selectedArt.label_en || selectedArt.label_de)}
                    </h3>
                    {selectedArt.braucht_genehmigung && !editingId && (
                      <p className="text-xs text-[var(--text-tertiary)]">{t('portal.absences.needsApproval')}</p>
                    )}
                  </div>
                </div>

                {/* Half day toggle (#1546 — nur Urlaub, NICHT Zeitausgleich) */}
                {selectedArt.key === 'urlaub' && (
                  <div className="flex items-center gap-4 mb-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="radio" name="tagTyp" checked={!form.halber_tag} onChange={() => setForm(f => ({ ...f, halber_tag: false }))} className="accent-[#338edc]" />
                      <span className="text-sm text-[var(--text-primary)]">{t('portal.absences.fullDay')}</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="radio" name="tagTyp" checked={form.halber_tag} onChange={() => setForm(f => ({ ...f, halber_tag: true, bis: f.von }))} className="accent-[#338edc]" />
                      <span className="text-sm text-[var(--text-primary)]">{t('portal.absences.halfDay')}</span>
                    </label>
                  </div>
                )}

                {/* #927 — Stundenweise (z.B. Arztbesuch): Ganztägig-Toggle + Time-Felder */}
                {selectedArt.erlaubt_stundenweise && (
                  <div className="border border-[var(--border-default,#E5E7EB)] rounded-lg p-3 mb-4 bg-[var(--bg-inset,#F8F9FC)]">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={ganztaegig}
                        onChange={e => setGanztaegig(e.target.checked)}
                        className="accent-[#338edc] w-4 h-4"
                      />
                      <span className="text-sm font-medium text-[var(--text-primary)]">{t('absence.full_day', 'Ganztägig')}</span>
                      <span className="text-xs text-[var(--text-tertiary)]">— {t('absence.full_day_hint', 'Zeit-Erfassung deaktiviert')}</span>
                    </label>
                    {!ganztaegig && (
                      <div className="grid grid-cols-2 gap-3 mt-3">
                        <div>
                          <label className="text-xs font-medium text-[var(--text-secondary)] mb-1 block">{t('absence.time_from', 'Von Uhrzeit')}</label>
                          <input
                            type="time"
                            value={vonZeit}
                            onChange={e => setVonZeit(e.target.value)}
                            className="w-full px-3 py-2 border border-[var(--border-default,#E5E7EB)] rounded-lg text-sm bg-[var(--bg-card,#fff)] text-[var(--text-primary)] focus:outline-none focus:border-[#338edc] focus:ring-1 focus:ring-[#338edc]/20"
                          />
                        </div>
                        <div>
                          <label className="text-xs font-medium text-[var(--text-secondary)] mb-1 block">{t('absence.time_to', 'Bis Uhrzeit')}</label>
                          <input
                            type="time"
                            value={bisZeit}
                            onChange={e => setBisZeit(e.target.value)}
                            className="w-full px-3 py-2 border border-[var(--border-default,#E5E7EB)] rounded-lg text-sm bg-[var(--bg-card,#fff)] text-[var(--text-primary)] focus:outline-none focus:border-[#338edc] focus:ring-1 focus:ring-[#338edc]/20"
                          />
                        </div>
                      </div>
                    )}
                    {!ganztaegig && dauerH != null && dauerH > 0 && (
                      <p className="text-xs text-[var(--text-secondary)] mt-2">
                        {t('absence.duration_label', 'Dauer')}: {dauerH.toFixed(2).replace('.', ',')} h — {t('absence.counts_as_present', 'zählt als Anwesenheit, bezahlt')}
                      </p>
                    )}
                  </div>
                )}

                {/* Date fields */}
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="text-xs font-medium text-[var(--text-secondary)] mb-1 block">{t('portal.absences.from')}</label>
                    <input
                      type="date"
                      value={form.von}
                      onChange={e => setForm(f => ({ ...f, von: e.target.value, bis: f.halber_tag ? e.target.value : f.bis }))}
                      className="w-full px-3 py-2 border border-[var(--border-default,#E5E7EB)] rounded-lg text-sm bg-[var(--bg-card,#fff)] text-[var(--text-primary)] focus:outline-none focus:border-[#338edc] focus:ring-1 focus:ring-[#338edc]/20"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-[var(--text-secondary)] mb-1 block">{t('portal.absences.to')}</label>
                    <input
                      type="date"
                      value={form.bis}
                      min={form.von}
                      disabled={form.halber_tag}
                      onChange={e => setForm(f => ({ ...f, bis: e.target.value }))}
                      className="w-full px-3 py-2 border border-[var(--border-default,#E5E7EB)] rounded-lg text-sm bg-[var(--bg-card,#fff)] text-[var(--text-primary)] focus:outline-none focus:border-[#338edc] focus:ring-1 focus:ring-[#338edc]/20 disabled:opacity-50"
                    />
                  </div>
                </div>

                {/* Days calculation + projected saldo (v2.289.0) */}
                {berechneTage != null && (
                  <div className={`rounded-lg p-3 mb-4 text-sm space-y-1 ${berechneTage === 0 ? 'bg-amber-50 border border-amber-200' : 'bg-[var(--bg-inset,#F8F9FC)]'}`}>
                    <div className="flex justify-between text-[var(--text-secondary)]">
                      <span>{t('portal.absences.workingDays')}</span>
                      <span className="font-mono font-bold text-[var(--text-primary)]">
                        {berechneTage === 0.5 ? `0,5 ${t('portal.absences.day')}` : `${berechneTage} ${t('portal.absences.dayPlural')}`}
                      </span>
                    </div>
                    {berechneTage === 0 && (
                      <p className="text-xs text-amber-700 mt-1.5">{t('portal.absences.noWorkingDays')}</p>
                    )}
                    {projectedSaldo && (selectedArt?.key === 'urlaub' || selectedArt?.key === 'zeitausgleich' || !selectedArt) && berechneTage > 0 && (() => {
                      // #1737: planbar inkl. dynamischem Vorgriff — konsistent zur BE-Antragsvalidierung
                      const aktuellAm = Number(projectedSaldo.verfuegbar_planbar ?? projectedSaldo.aktuell_verfuegbar ?? projectedSaldo.saldo ?? 0);
                      // #1698: Preview basiert auf der UJ-Prognose (JE-Kachel entfernt)
                      const prognoseBasis = Number(projectedSaldo.prognose_urlaubsjahr ?? projectedSaldo.projiziert_jahresende ?? projectedSaldo.saldo ?? 0);
                      const nachAntrag = prognoseBasis - berechneTage;
                      return (
                        <>
                          <div className="flex justify-between">
                            <span className="text-[var(--text-secondary)]">{form.von ? t('urlaub.aufgebautBis', { datum: new Date(form.von + 'T00:00:00').toLocaleDateString('de-AT') }) : t('urlaub.aufgebaut')}</span>
                            <span className="font-medium text-[var(--text-primary)]">{parseFloat(projectedSaldo.aufgebaut || 0).toFixed(2)}</span>
                          </div>
                          <div className="flex justify-between border-t border-[var(--border-default)] pt-1 mt-1">
                            <span className="font-semibold text-[var(--text-primary)]">{form.von ? t('urlaub.verfuegbarAm', { datum: new Date(form.von + 'T00:00:00').toLocaleDateString('de-AT') }) : t('portal.absences.currentAvailable')}</span>
                            <span className={`font-bold ${aktuellAm < 0 ? 'text-red-600' : 'text-[var(--text-primary)]'}`}>{Math.round(aktuellAm * 100) / 100}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-[var(--text-secondary)]">{t('portal.absences.projectedAfter')}</span>
                            <span className={`font-bold ${nachAntrag < 0 ? 'text-red-600' : 'text-emerald-600'}`}>{Math.round(nachAntrag * 100) / 100}</span>
                          </div>
                          {nachAntrag >= 0 && (
                            <p className="text-xs text-emerald-600 mt-1">{t('urlaub.prognoseOk')}</p>
                          )}
                          {nachAntrag < 0 && (
                            <p className="text-xs text-red-600 mt-1">{t('urlaub.nichtGenugTage')}</p>
                          )}
                        </>
                      );
                    })()}
                  </div>
                )}

                {/* File upload for Krankenstand types */}
                {(selectedArt.key === 'krankenstand' || selectedArt.benoetigt_bestaetigung_ab_tage) && (
                  <div className="mb-4">
                    <label className="text-xs font-medium text-[var(--text-secondary)] mb-1 block">
                      {t('portal.absences.medicalCertificate')} ({t('common.optional')})
                    </label>
                    <div className="flex items-center gap-3">
                      <label className="flex items-center gap-2 px-4 py-2 rounded-lg border border-[var(--border-default,#E5E7EB)] cursor-pointer hover:bg-[var(--bg-inset,#F8F9FC)] text-sm text-[var(--text-secondary)] transition-colors">
                        <Upload size={16} />
                        {uploadFile ? uploadFile.name : t('portal.absences.selectFile')}
                        <input type="file" accept=".pdf,.jpg,.jpeg,.png" className="hidden" onChange={e => setUploadFile(e.target.files?.[0] || null)} />
                      </label>
                      {uploadFile && (
                        <button onClick={() => setUploadFile(null)} className="text-[var(--text-tertiary)] hover:text-[var(--text-primary)]">
                          <X size={16} />
                        </button>
                      )}
                    </div>
                  </div>
                )}

                {/* Note */}
                <div className="mb-4">
                  <label className="text-xs font-medium text-[var(--text-secondary)] mb-1 block">{t('portal.absences.note')} ({t('common.optional')})</label>
                  <input
                    type="text"
                    value={form.notiz}
                    onChange={e => setForm(f => ({ ...f, notiz: e.target.value }))}
                    placeholder={t('portal.absences.notePlaceholder')}
                    className="w-full px-3 py-2 border border-[var(--border-default,#E5E7EB)] rounded-lg text-sm bg-[var(--bg-card,#fff)] text-[var(--text-primary)] placeholder-[var(--text-tertiary)] focus:outline-none focus:border-[#338edc] focus:ring-1 focus:ring-[#338edc]/20"
                  />
                </div>

                {submitError && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4 flex items-start gap-2">
                    <AlertCircle size={16} className="text-red-500 mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-red-800">{submitError}</p>
                  </div>
                )}

                {/* Actions */}
                <div className="flex justify-end gap-3 pt-2 border-t border-[var(--border-default,#E5E7EB)]">
                  <button
                    onClick={() => setSelectedArt(null)}
                    className="px-4 py-2 rounded-[10px] border border-[var(--border-default,#E5E7EB)] text-sm font-medium text-[var(--text-secondary)] hover:bg-[var(--bg-inset,#F8F9FC)]"
                  >
                    {t('common.cancel')}
                  </button>
                  <button
                    onClick={handleSubmit}
                    disabled={submitting || !form.von || (!form.halber_tag && !form.bis) || berechneTage == null || berechneTage <= 0}
                    className="px-4 py-2 rounded-[10px] bg-[#338edc] hover:bg-[#2b7bc4] text-white text-sm font-semibold disabled:opacity-50 transition-colors"
                  >
                    {submitting ? t('portal.absences.submitting') : t('portal.absences.submitRequest')}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── TAB 3: Team-Kalender ── */}
      {tab === 'kalender' && (
        <div className="border border-[var(--border-default,#E5E7EB)] rounded-xl p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-[var(--text-primary)] uppercase tracking-wider flex items-center gap-2">
              <Users className="w-4 h-4" /> {t('portal.absences.teamCalendar')}
            </h3>
            <div className="flex items-center gap-2">
              <button onClick={() => {
                const [y, m] = kalenderMonat.split('-').map(Number);
                const prev = m === 1 ? `${y - 1}-12` : `${y}-${String(m - 1).padStart(2, '0')}`;
                setKalenderMonat(prev);
              }} className="p-1.5 border border-[var(--border-default,#E5E7EB)] rounded-lg hover:bg-[var(--bg-inset,#F8F9FC)]">
                <ChevronLeft size={16} className="text-[var(--text-secondary)]" />
              </button>
              <span className="text-sm font-medium text-[var(--text-primary)] min-w-[120px] text-center">
                {t(`common.months.${parseInt(kalenderMonat.split('-')[1]) - 1}`)} {kalenderMonat.split('-')[0]}
              </span>
              <button onClick={() => {
                const [y, m] = kalenderMonat.split('-').map(Number);
                const next = m === 12 ? `${y + 1}-01` : `${y}-${String(m + 1).padStart(2, '0')}`;
                setKalenderMonat(next);
              }} className="p-1.5 border border-[var(--border-default,#E5E7EB)] rounded-lg hover:bg-[var(--bg-inset,#F8F9FC)]">
                <ChevronRight size={16} className="text-[var(--text-secondary)]" />
              </button>
            </div>
          </div>
          {kalenderData ? (
            <div className="grid grid-cols-7 gap-1">
              {[t('common.weekdays.mo'), t('common.weekdays.tu'), t('common.weekdays.we'), t('common.weekdays.th'), t('common.weekdays.fr'), t('common.weekdays.sa'), t('common.weekdays.su')].map(d => (
                <div key={d} className="text-center text-[10px] text-[var(--text-tertiary)] font-semibold py-1">{d}</div>
              ))}
              {(() => {
                const [y, m] = kalenderMonat.split('-').map(Number);
                const firstDay = new Date(y, m - 1, 1).getDay();
                const offset = firstDay === 0 ? 6 : firstDay - 1;
                const daysInMonth = new Date(y, m, 0).getDate();
                const cells = [];
                for (let i = 0; i < offset; i++) cells.push(<div key={`e${i}`} />);
                for (let d = 1; d <= daysInMonth; d++) {
                  const ds = `${kalenderMonat}-${String(d).padStart(2, '0')}`;
                  const info = kalenderData.tage?.[ds] || { anzahl: 0, personen: [] };
                  const isWeekend = new Date(y, m - 1, d).getDay() === 0 || new Date(y, m - 1, d).getDay() === 6;
                  const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
                  const isToday = ds === todayStr;
                  cells.push(
                    <div key={d} className={`text-center rounded-lg p-1.5 min-h-[44px] bg-[var(--bg-inset,#F8F9FC)] ${isWeekend ? 'opacity-50' : ''} ${isToday ? 'ring-2 ring-[#338edc]' : ''}`} title={info.personen?.map(p => `${p.vorname} ${p.nachname}`).join(', ') || ''}>
                      <div className="text-xs text-[var(--text-secondary)]">{d}</div>
                      {info.anzahl > 0 && !isWeekend && (
                        <div className={`text-[10px] font-bold mt-0.5 ${info.anzahl >= 3 ? 'text-red-600' : info.anzahl >= 2 ? 'text-amber-600' : 'text-emerald-600'}`}>
                          {info.anzahl} {t('portal.absences.absent')}
                        </div>
                      )}
                    </div>
                  );
                }
                return cells;
              })()}
            </div>
          ) : (
            <div className="text-center text-[var(--text-tertiary)] text-sm py-8">{t('common.loading')}</div>
          )}
          {kalenderData?.namen_sichtbar && (
            <div className="text-[10px] text-[var(--text-tertiary)] mt-3">{t('portal.absences.namesVisible')}</div>
          )}
        </div>
      )}

      <ConfirmDialog
        open={!!confirmDialog}
        title={confirmDialog?.title}
        message={confirmDialog?.message}
        variant={confirmDialog?.variant}
        confirmLabel={confirmDialog?.confirmLabel}
        cancelLabel={confirmDialog?.cancelLabel}
        onConfirm={confirmDialog?.onConfirm}
        onCancel={() => setConfirmDialog(null)}
      />
    </div>
  );
}
